import { StatusBar } from 'expo-status-bar';
import React from 'react';
import { Appearance, StatusBar as NativeStatusBar, Platform } from 'react-native';
import { Provider as PaperProvider } from 'react-native-paper';

import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { ThemeToggle, getAppTheme, getNavigationTheme, getPaperTheme } from '@helpers/Theme';
import type { AppTheme } from '@helpers/Theme';

export { ThemeToggle } from '@helpers/Theme';

export function getInitialTheme() {
  return Appearance.getColorScheme() === 'dark' ? ThemeToggle.DARK : ThemeToggle.LIGHT;
}

export type ThemeContextProps = {
  /** Currently selected app color scheme. */
  theme: ThemeToggle;
  /** Updates the selected app color scheme. */
  setTheme: React.Dispatch<React.SetStateAction<ThemeToggle>>;
};

export const ThemeContext = createNamedContext<ThemeContextProps>({
  ThemeContext: undefined,
});

export function ThemeProvider({ children }: React.PropsWithChildren) {
  const [theme, setTheme] = React.useState<ThemeToggle>(getInitialTheme());

  const value = React.useMemo(() => ({ theme, setTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useRequiredContext(ThemeContext);
}

export function useAppTheme(): AppTheme {
  const { theme } = useTheme();
  return getAppTheme(theme);
}

export function usePaperTheme() {
  const { theme } = useTheme();
  return getPaperTheme(theme);
}

export function AppPaperProvider({ children }: React.PropsWithChildren) {
  const paperTheme = usePaperTheme();

  return <PaperProvider theme={paperTheme}>{children}</PaperProvider>;
}

export function useNavigationTheme() {
  const { theme } = useTheme();
  return getNavigationTheme(theme);
}

function setStatusBarStyleByTheme(theme: ThemeToggle, backgroundColor: string) {
  switch (theme) {
    case ThemeToggle.LIGHT:
      StatusBar.setStyle('dark');
      if (Platform.OS === 'android') {
        NativeStatusBar.setBackgroundColor(backgroundColor, true);
      }
      break;
    case ThemeToggle.DARK:
      StatusBar.setStyle('light');
      if (Platform.OS === 'android') {
        NativeStatusBar.setBackgroundColor(backgroundColor, true);
      }
      break;
    default:
      StatusBar.setStyle('auto');
      break;
  }
}

export function useStatusBarTheme() {
  const { theme } = useTheme();
  const appTheme = useAppTheme();

  React.useEffect(() => {
    setStatusBarStyleByTheme(theme, appTheme.colors.surface);
  }, [theme, appTheme.colors.surface]);
}
